if (!customElements.get('testimonials-carousel')) {
  class TestimonialsCarousel extends HTMLElement {
    static positions = new Map();

    connectedCallback() {
      this.track = this.querySelector('[data-testimonials-track]');
      this.slides = [...this.querySelectorAll('[data-testimonials-slide]')];
      this.dots = [...this.querySelectorAll('[data-testimonials-dot]')];
      this.storageKey = `testimonials:${this.dataset.sectionId}`;
      this.delay = Number(this.dataset.autoplayDelay) || 5000;
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.currentIndex = this.readPosition();
      this.timer = null;
      this.frame = null;
      this.paused = false;

      this.onScroll = this.handleScroll.bind(this);
      this.onResize = this.handleResize.bind(this);
      this.onMouseEnter = () => this.setPaused(true);
      this.onMouseLeave = () => this.setPaused(false);
      this.onFocusIn = () => this.setPaused(true);
      this.onFocusOut = (event) => {
        if (!this.contains(event.relatedTarget)) this.setPaused(false);
      };
      this.onVisibilityChange = () => document.hidden ? this.clearTimer() : this.schedule();
      this.onReducedMotionChange = () => this.schedule();
      this.onBlockSelect = this.handleBlockSelect.bind(this);

      this.track?.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      window.addEventListener('resize', this.onResize);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.reducedMotion.addEventListener('change', this.onReducedMotionChange);
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goToKey(dot.dataset.testimonialsDot, true)));

      requestAnimationFrame(() => {
        this.goTo(this.currentIndex, false);
        this.schedule();
      });
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.reducedMotion.removeEventListener('change', this.onReducedMotionChange);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.clearTimer();
    }

    readPosition() {
      const key = TestimonialsCarousel.positions.get(this.storageKey);
      const index = this.slides.findIndex((slide) => slide.dataset.slideKey === key);
      return index >= 0 ? index : 0;
    }

    rememberPosition(index) {
      this.currentIndex = Math.min(Math.max(index, 0), Math.max(this.slides.length - 1, 0));
      const key = this.slides[this.currentIndex]?.dataset.slideKey;
      if (key) TestimonialsCarousel.positions.set(this.storageKey, key);
    }

    goToKey(key, smooth) {
      const index = this.slides.findIndex((slide) => slide.dataset.slideKey === key);
      if (index >= 0) this.goTo(index, smooth);
    }

    goTo(index, smooth) {
      if (!this.track || !this.slides.length) return;
      this.rememberPosition(index);
      this.track.scrollTo({ left: this.currentIndex * this.track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
      this.updateState();
      this.schedule();
    }

    handleScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const index = Math.round(this.track.scrollLeft / Math.max(this.track.clientWidth, 1));
        if (index !== this.currentIndex) {
          this.rememberPosition(index);
          this.updateState();
        }
        this.schedule();
      });
    }

    handleResize() {
      requestAnimationFrame(() => this.goTo(this.currentIndex, false));
    }

    handleBlockSelect(event) {
      const selected = event.target.closest('[data-slide-key]');
      if (!selected || !this.contains(selected)) return;
      this.setPaused(true);
      this.goToKey(selected.dataset.slideKey, true);
    }

    setPaused(paused) {
      this.paused = paused;
      paused ? this.clearTimer() : this.schedule();
    }

    clearTimer() {
      if (this.timer) clearTimeout(this.timer);
      this.timer = null;
    }

    schedule() {
      this.clearTimer();
      if (!this.autoplayEnabled || this.paused || document.hidden || this.reducedMotion.matches || this.slides.length < 2) return;
      this.timer = setTimeout(() => this.goTo((this.currentIndex + 1) % this.slides.length, true), this.delay);
    }

    updateState() {
      const key = this.slides[this.currentIndex]?.dataset.slideKey;
      this.dots.forEach((dot) => dot.setAttribute('aria-current', String(dot.dataset.testimonialsDot === key)));
    }
  }

  customElements.define('testimonials-carousel', TestimonialsCarousel);
}
